import React from 'react';
import { ArrowRight, CheckCircle, Database, Zap, Shield, Users, Cloud, Server, Network } from 'lucide-react';
import Layout from '../../layouts/Layout';
import CTA from '../../components/CTA';
import { Link } from 'react-router-dom';

const SystemArchitecturePage = () => {
    const architectureServices = [
        {
            icon: <Database className="w-6 h-6" />,
            title: "Database Design & Optimisation",
            description: "Scalable database architectures with performance tuning and data integrity.",
            features: ["PostgreSQL/MySQL design", "Query optimisation", "Data modelling", "Backup strategies"]
        },
        {
            icon: <Cloud className="w-6 h-6" />,
            title: "Cloud Infrastructure",
            description: "AWS and Azure cloud solutions with auto-scaling and high availability.",
            features: ["Cloud migration", "Auto-scaling", "Load balancing", "Disaster recovery"]
        },
        {
            icon: <Server className="w-6 h-6" />,
            title: "Backend Systems",
            description: "Robust API development and microservices architecture for enterprise applications.",
            features: ["RESTful APIs", "Microservices", "Message queues", "Event-driven systems"]
        },
        {
            icon: <Network className="w-6 h-6" />,
            title: "System Integration",
            description: "Seamless integration between existing systems and new applications.",
            features: ["Third-party APIs", "Data synchronisation", "Legacy system integration", "Enterprise service bus"]
        }
    ];

    const features = [
        {
            icon: <Zap className="w-6 h-6" />,
            title: "High Performance",
            description: "Architectures designed for optimal performance with caching strategies, CDNs, and efficient data processing."
        },
        {
            icon: <Shield className="w-6 h-6" />,
            title: "Enterprise Security",
            description: "Multi-layered security with encryption, access controls, and compliance with industry standards."
        },
        {
            icon: <Users className="w-6 h-6" />,
            title: "Scalable Solutions",
            description: "Future-proof architectures that scale with your business growth and changing requirements."
        },
        {
            icon: <Database className="w-6 h-6" />,
            title: "Data Integrity",
            description: "Robust data management with backup strategies, versioning, and consistency across systems."
        }
    ];

    const technologies = [
        {
            category: "Cloud Platforms",
            items: ["Amazon Web Services", "Microsoft Azure", "Google Cloud Platform", "DigitalOcean", "Cloudflare", "Vercel"]
        },
        {
            category: "Databases",
            items: ["PostgreSQL", "MySQL", "MongoDB", "Redis", "Elasticsearch", "DynamoDB"]
        },
        {
            category: "Backend Technologies",
            items: ["Node.js", "Python", "PHP", "Go", "Docker", "Kubernetes"]
        },
        {
            category: "DevOps & Monitoring",
            items: ["CI/CD Pipelines", "Terraform", "Prometheus", "Grafana", "ELK Stack", "New Relic"]
        }
    ];

    const solutions = [
        {
            title: "Enterprise APIs",
            description: "Scalable RESTful and GraphQL APIs for enterprise applications",
            capabilities: [
                "RESTful API design",
                "GraphQL implementations",
                "API versioning strategies",
                "Rate limiting and throttling",
                "Comprehensive documentation",
                "Authentication and authorisation"
            ]
        },
        {
            title: "Microservices Architecture",
            description: "Distributed systems designed for scalability and maintainability",
            capabilities: [
                "Service decomposition",
                "Inter-service communication",
                "Event-driven architecture",
                "Service discovery",
                "Circuit breaker patterns",
                "Distributed tracing"
            ]
        },
        {
            title: "Cloud Infrastructure",
            description: "Robust cloud solutions with high availability and disaster recovery",
            capabilities: [
                "Infrastructure as Code",
                "Auto-scaling groups",
                "Load balancer configuration", 
                "Multi-region deployment",
                "Backup and recovery",
                "Cost optimisation"
            ]
        },
        {
            title: "Data Pipeline Architecture",
            description: "ETL/ELT pipelines for data processing and analytics",
            capabilities: [
                "Data ingestion systems",
                "Real-time stream processing",
                "Batch processing workflows",
                "Data warehouse design",
                "Business intelligence integration",
                "Data quality monitoring"
            ]
        }
    ];

    const process = [
        {
            step: "01",
            title: "Requirements Analysis",
            description: "We conduct thorough analysis of your current systems, performance requirements, and business objectives to design the optimal architecture."
        },
        {
            step: "02",
            title: "Architecture Design",
            description: "Our architects create detailed system designs with component diagrams, data flow models, and technology recommendations."
        },
        {
            step: "03",
            title: "Proof of Concept",
            description: "We build and test critical components to validate architecture decisions and performance characteristics before full implementation."
        },
        {
            step: "04",
            title: "Implementation & Migration",
            description: "Systematic implementation with minimal downtime, including data migration strategies and rollback procedures."
        },
        {
            step: "05",
            title: "Monitoring & Optimisation",
            description: "Continuous monitoring, performance tuning, and iterative improvements to ensure optimal system performance."
        }
    ];

    const benefits = [
        "Scalable architectures that grow with your business",
        "High availability systems with 99.9% uptime guarantees",
        "Performance optimisation for faster response times",
        "Robust security measures and compliance frameworks",
        "Cloud-native solutions with cost optimisation",
        "API-first design for seamless integrations",
        "Comprehensive monitoring and alerting systems",
        "Documentation and knowledge transfer"
    ];

    const useCases = [
        {
            title: "E-commerce Platforms",
            description: "High-traffic retail systems with payment processing and inventory management"
        },
        {
            title: "Financial Services",
            description: "Secure trading platforms and banking systems with regulatory compliance"
        },
        {
            title: "Healthcare Systems",
            description: "HIPAA-compliant patient management and medical record systems"
        },
        {
            title: "IoT & Manufacturing",
            description: "Industrial IoT platforms with real-time data processing and analytics"
        },
        {
            title: "SaaS Applications",
            description: "Multi-tenant software platforms with subscription management"
        },
        {
            title: "Media & Content",
            description: "Content delivery networks and video streaming platforms"
        }
    ];

    return (
        <Layout
            title="System Architecture Services"
            description="Enterprise system architecture and backend development services in the UK. Scalable cloud infrastructure, database design, API development, and microservices architecture."
            keywords="system architecture UK, backend development, cloud infrastructure, database design, API development, microservices, enterprise architecture"
            path="/services/system-architecture"
        >
            <div className="min-h-screen bg-white">
                {/* Hero Section */}
                <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-slate-50 to-slate-100">
                    <div className="max-w-6xl mx-auto">
                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                            <div>
                                <div className="inline-flex items-center px-4 py-2 bg-slate-900 text-white rounded-full text-sm font-medium mb-8">
                                    <Database className="w-4 h-4 mr-2" />
                                    System Architecture
                                </div>
                                
                                <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-8 leading-tight">
                                    Enterprise-Grade
                                    <span className="block text-slate-700 mt-2">System Architecture</span>
                                </h1>
                                
                                <p className="text-xl text-slate-600 mb-10 leading-relaxed">
                                    Design and build scalable, secure backend systems that power your business. Our enterprise 
                                    architecture services include cloud infrastructure, database design, API development, and 
                                    microservices solutions.
                                </p>
                                
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <Link to="/schedule-consultation" className="bg-slate-900 text-white px-8 py-4 rounded-xl font-semibold hover:bg-slate-800 transition-all hover:shadow-lg flex items-center justify-center">
                                        Discuss Your Architecture <ArrowRight className="ml-2 w-5 h-5" />
                                    </Link>
                                    {/* <Link className="border-2 border-slate-200 text-slate-700 px-8 py-4 rounded-xl font-semibold hover:border-slate-300 hover:bg-slate-50 transition-all">
                                        View Case Studies
                                    </Link> */}
                                </div>
                            </div>
                            
                            <div className="relative">
                                <div className="bg-white rounded-3xl p-8 shadow-2xl border border-slate-200">
                                    <div className="space-y-6">
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">System Uptime</span>
                                            <span className="text-slate-900 font-semibold">99.99%</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">API Response Time</span>
                                            <span className="text-slate-900 font-semibold">&lt;100ms</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">Scalability</span>
                                            <span className="text-slate-900 font-semibold">Auto-scaling</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">Security Rating</span>
                                            <span className="text-slate-900 font-semibold">Enterprise</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Architecture Services Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">System Architecture Services</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Comprehensive backend and infrastructure solutions for enterprise applications
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-8">
                            {architectureServices.map((service, index) => (
                                <div key={index} className="bg-slate-50 rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
                                    <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white mb-6">
                                        {service.icon}
                                    </div>
                                    <h3 className="text-2xl font-bold text-slate-900 mb-4">{service.title}</h3>
                                    <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>
                                    <div className="grid grid-cols-2 gap-3">
                                        {service.features.map((feature, featureIndex) => (
                                            <div key={featureIndex} className="flex items-center">
                                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                                <span className="text-slate-700 text-sm">{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Features Section */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Why Choose Our System Architecture?</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Enterprise-grade solutions built for performance, security, and scalability
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {features.map((feature, index) => (
                                <div key={index} className="bg-white rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
                                    <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white mb-6">
                                        {feature.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-4">{feature.title}</h3>
                                    <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Solutions Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Architecture Solutions</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Comprehensive backend solutions tailored to your business requirements
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-8">
                            {solutions.map((solution, index) => (
                                <div key={index} className="bg-slate-50 rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
                                    <h3 className="text-2xl font-bold text-slate-900 mb-4">{solution.title}</h3>
                                    <p className="text-slate-600 mb-6 leading-relaxed">{solution.description}</p>
                                    <div className="space-y-3">
                                        {solution.capabilities.map((capability, capabilityIndex) => (
                                            <div key={capabilityIndex} className="flex items-center">
                                                <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                                <span className="text-slate-700">{capability}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Technologies Section */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Technology Stack</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Industry-leading technologies for robust system architecture
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {technologies.map((tech, index) => (
                                <div key={index} className="bg-white rounded-2xl p-6 border border-slate-200">
                                    <h3 className="text-lg font-bold text-slate-900 mb-4">{tech.category}</h3>
                                    <div className="space-y-2">
                                        {tech.items.map((item, itemIndex) => (
                                            <div key={itemIndex} className="flex items-center">
                                                <div className="w-2 h-2 bg-slate-900 rounded-full mr-3"></div>
                                                <span className="text-slate-700 text-sm">{item}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Process Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Our Architecture Process</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                A systematic approach to designing and implementing enterprise systems
                            </p>
                        </div>
                        
                        <div className="space-y-8">
                            {process.map((step, index) => (
                                <div key={index} className="flex items-start">
                                    <div className="flex-shrink-0 w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white font-bold text-lg mr-6">
                                        {step.step}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-2xl font-bold text-slate-900 mb-4">{step.title}</h3>
                                        <p className="text-lg text-slate-600 leading-relaxed">{step.description}</p>
                                    </div>
                                    {/* {index < process.length - 1 && (
                                        <ChevronRight className="w-6 h-6 text-slate-400 ml-6" />
                                    )} */}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Benefits & Use Cases Section */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="grid lg:grid-cols-2 gap-16 items-center">
                            <div>
                                <h2 className="text-4xl font-bold text-slate-900 mb-8">What You Get</h2>
                                <p className="text-xl text-slate-600 mb-10 leading-relaxed">
                                    Our system architecture services deliver enterprise-grade solutions that provide 
                                    the foundation for scalable, secure, and high-performance applications.
                                </p>
                                
                                <div className="space-y-4">
                                    {benefits.map((benefit, index) => (
                                        <div key={index} className="flex items-start">
                                            <CheckCircle className="w-6 h-6 text-green-600 mr-4 flex-shrink-0 mt-0.5" />
                                            <span className="text-slate-700 text-lg">{benefit}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-lg">
                                <h3 className="text-2xl font-bold text-slate-900 mb-6">Common Use Cases</h3>
                                <div className="space-y-4">
                                    {useCases.map((useCase, index) => (
                                        <div key={index} className="p-4 bg-slate-50 rounded-lg">
                                            <h4 className="font-semibold text-slate-900 mb-2">{useCase.title}</h4>
                                            <p className="text-slate-600 text-sm">{useCase.description}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Performance Stats */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="bg-slate-900 rounded-3xl p-12 text-white">
                            <div className="text-center mb-12">
                                <h2 className="text-4xl font-bold mb-6">System Performance Metrics</h2>
                                <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                                    Our architectures consistently deliver exceptional performance and reliability
                                </p>
                            </div>
                            
                            <div className="grid md:grid-cols-4 gap-8 text-center">
                                <div>
                                    <div className="text-4xl font-bold mb-2">99.99%</div>
                                    <div className="text-slate-300">System Uptime</div>
                                </div>
                                <div>
                                    <div className="text-4xl font-bold mb-2">&lt;100ms</div>
                                    <div className="text-slate-300">API Response</div>
                                </div>
                                <div>
                                    <div className="text-4xl font-bold mb-2">10,000+</div>
                                    <div className="text-slate-300">Concurrent Users</div>
                                </div>
                                <div>
                                    <div className="text-4xl font-bold mb-2">Auto</div>
                                    <div className="text-slate-300">Scaling</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <CTA />
            </div>
        </Layout>
    );
};

export default SystemArchitecturePage;